<script setup lang="ts">
import type { PropType } from '@mimir/web/compat/vueCompat';
import { computed, ref, getCurrentInstance } from '@mimir/web/compat/vueCompat';
import type { UserInfoDto } from '@mimir/common/dto/userDto';
import { VAvatar, VImg } from 'vuetify/lib';

const vm = getCurrentInstance()?.proxy as Vue;

const props = defineProps({
  user: {
    type: [Object, String] as PropType<UserInfoDto | null | string>,
    required: true,
  },
  small: {
    type: Boolean,
    default: false,
  },
  xSmall: {
    type: Boolean,
    default: false,
  }
});

const imgLoadFailed = ref(false);
const userDto = computed(() => (typeof props.user === 'string' ? allUsersById.value[props.user] : props.user) ?? null);
const initials = computed(() => {
  if (!userDto.value) {
    return 'Unknown user';
  }
  // We only care about first initial and last initial. We avoid complexity of middle-names for now.
  const firstInitial = userDto.value.givenName?.charAt(0) || '';
  const lastNames = userDto.value.familyName?.split(' ') || [];
  const lastInitial = lastNames[lastNames.length - 1]?.charAt(0) || '';
  return (firstInitial + lastInitial || userDto.value.email.charAt(0)).toUpperCase();
});
const allUsersById = computed(() => vm.$store.getters.allUsersById as Record<string, UserInfoDto>);
</script>

<template>
  <VAvatar
    :size="xSmall ? 12 : small ? 20 : 24"
    v-on="$listeners"
  >
    <VImg
      v-if="userDto && userDto.avatar && !imgLoadFailed"
      class="profile-image"
      :alt="userDto && userDto.email ? userDto.email : undefined"
      :src="userDto.avatar"
      @error="imgLoadFailed = true"
    />
    <VIcon
      v-else-if="!userDto"
      x-small
      class="user-avatar"
    >person</VIcon>
    <div
      v-else
      :class="['user-avatar', small && 'small', xSmall && 'xSmall']"
    >
      <span>{{ initials }}</span>
    </div>
  </VAvatar>
</template>

<style lang="stylus">
  .user-avatar
    border-radius: 50%
    display: flex
    width: 36px
    height: 36px
    background: #0d4f5e
    color: white
    span
      margin: auto
      font-size: 11px
    &.small
      width: 24px
      height: 24px
      span
        font-size: 10px !important
    &.xSmall
      width: 16px
      height: 16px
      span
        font-size: 7px !important
</style>
